
/**
 * GenericImage Component
 * Generic Image Component is used to display images.
 * This component can be used both as web component or vue component.
 * Apart from that GenericImage can be used with slots or just by injecting the data in the component registry.
 * The data in the registry for this component extends the GenericImage interface but adds a ratio property
 * for cases when the image ratio is on specs by default
 * If no ratio is provided on GenericImage Object or via ratio property on data
 * it will default to <picture> element and ratio will be defined by the image for each mq.
 * If ratio is provided it will fallback to <wb-aspect-ratio> component and it will have the same ratio for
 * every breakpoint.
 * {@link https://dev.assets.oneweb.mercedes-benz.com/plugin/mbmxp/owc/apilayer/latest/html/index.html#tag/ComponentGenericImage}
 * {@link https://pages.git.i.mercedes-benz.com/dh-io-workbench/documentation/v5/components/image/}
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import GenericImageGenerator from '@owc-utils/GenericImage/GenericImageGenerator';
import { DataJson, GenericImageComponent, Mode } from './GenericImage';

const genericImageModes: Mode[] = ['image', 'picture'];

export default (ComponentInitializeDispatcher as GenericImageComponent).extend({
  name: 'owc-generic-image',
  components: {
    SlotContainer,
  },
  props: {
    /**
     * Used in cases where the parent is not a webcomponent, so this prop
     * needs to be set manually
     */
    isAuthorEditMode: {
      type: Boolean,
      required: false,
    },
    loadEager: {
      type: Boolean,
      default: false,
    },
    imageData: {
      type: Object,
      required: false,
    },
  },
  mounted() {
    if (this.mode === 'slot') {
      const image = this.$el.querySelector('img');
      image?.addEventListener('load', () => {
        const src = image.currentSrc;
        const alt = image.getAttribute('alt');
        const data = { src, alt };
        this.slotData = data;
      });
    }
  },
  data() {
    return {
      slotData: { src: '', alt: '' },
    };
  },
  watch: {
    componentData(data) {
      if (data?.height) {
        this.$nextTick(() => {
          const image = this.$el.querySelector('img');
          if (image) {
            image.classList.add('owc-generic-image__fixed-height');
            image.style.setProperty('height', data.height);
          }
        });
      }
    },
  },
  computed: {
    mode: function() {
      let mode: Mode;
      if (this.genericImage?.imageData.ratio) {
        mode = 'image';
      } else if (this.shouldRenderPictureElement) {
        mode = 'picture';
      } else {
        mode = 'slot';
      }
      return mode;
    },
    /**
     * So as to have a stantard way of retrieving data from a component, JSON will output
     * data slot attribues in case the mode is set to slot, as this is necessary for
     * cross element communication, mode is also given, to allow consumers to properly
     * handle data.
     */
    json: function() {
      let payload = {};
      if (genericImageModes?.includes(this.mode)) {
        payload = this.genericImage || {};
      }
      return {
        mode: this.mode,
        ...payload,
      } as DataJson;
    },
    genericImage: function() {
      if (this.componentId && this.componentData) {
        if (Object.keys(this.componentData).length && this.componentData.genericImage) {
          const genericImageData = this.componentData.genericImage;
          /*
           * If the GenericImageGenerator is being used outside the GenericImage and passed provided in the registry we return it right away.
           * EG: cases where we need to exclude mq's and/or forceRatio and have deeper control of the image data
           *
           * If the registry contains the GenericImage data, we create one for you.
           * EG: usage of <owc-generic-image /> webcomponent
           */

          if (genericImageData.isGenerated) {
            return genericImageData;
          } else {
            return new GenericImageGenerator(genericImageData);
          }
        }
      } else {
        if (this.imageData) {
          const genericImageData = this.imageData;
          /*
           * If the GenericImageGenerator is being used outside the GenericImage and passed as imageData we return it right away.
           * EG: cases where we need to exclude mq's and/or forceRatio and have deeper control of the image data
           *
           * If the imageData is the GenericImage data, we create one for you.
           * EG: usage of <owc-generic-image /> webcomponent
           */
          if (genericImageData.isGenerated) {
            return genericImageData;
          } else {
            return new GenericImageGenerator(genericImageData);
          }
        }
      }
      return null;
    },
    /**
     * shouldRenderPictureElement
     * Ensures that GenericImage renders a picture element if ratio is not given
     * and pictureBreakpoints array is provided.
     *
     * @returns {boolean} if the GenericImage should render picture element
     */
    shouldRenderPictureElement: function(): boolean {
      return Boolean(
        this.genericImage &&
          !this.genericImage.imageData.ratio &&
          this.genericImage.pictureBreakpoints.length > 0,
      );
    },
    isEagerLoaded: function() {
      return this.isEditMode || this.isAuthorEditMode || this.loadEager;
    },
  },
});
